import { desktopBus, DesktopEvents } from "../commands/EventBus";

export interface FocusField {
  id: string;
  getElement: () => HTMLElement | null;
  disabled?: boolean;
  group?: string;
}

export interface FocusGroup {
  id: string;
  label?: string;
  order?: number;
}

export class FocusManager {
  private fields = new Map<string, FocusField>();
  private groups = new Map<string, FocusGroup>();
  private order: string[] = [];
  private activeId: string | null = null;
  private scope = "default";

  setScope(scope: string) {
    this.scope = scope || "default";
  }

  clear() {
    this.fields.clear();
    this.groups.clear();
    this.order = [];
    this.activeId = null;
  }

  registerGroup(group: FocusGroup) {
    this.groups.set(group.id, group);
  }

  registerField(field: FocusField, index?: number) {
    this.fields.set(field.id, field);
    if (typeof index === "number") {
      this.order.splice(index, 0, field.id);
    } else if (!this.order.includes(field.id)) {
      this.order.push(field.id);
    }

    return () => this.unregisterField(field.id);
  }

  setFields(fields: FocusField[]) {
    this.fields.clear();
    this.order = [];
    fields.forEach((field, i) => this.registerField(field, i));
  }

  unregisterField(id: string) {
    this.fields.delete(id);
    this.order = this.order.filter((value) => value !== id);
    if (this.activeId === id) this.activeId = null;
  }

  private enabledFields() {
    return this.order
      .map((id) => this.fields.get(id))
      .filter((field): field is FocusField => Boolean(field && !field.disabled));
  }

  focus(id: string) {
    const field = this.fields.get(id);
    if (!field || field.disabled) return false;
    const element = field.getElement?.();
    if (!element) return false;

    element.focus();
    if (typeof (element as HTMLInputElement).select === "function") {
      try {
        (element as HTMLInputElement).select();
      } catch {
        // no-op for non selectable targets
      }
    }

    this.activeId = id;
    desktopBus.emit(DesktopEvents.FOCUS_CHANGED, { id, scope: this.scope });
    return true;
  }

  focusFirst() {
    const fields = this.enabledFields();
    if (fields.length === 0) return false;
    return this.focus(fields[0].id);
  }

  focusNext() {
    const fields = this.enabledFields();
    if (fields.length === 0) return false;
    const index = fields.findIndex((field) => field.id === this.activeId);
    const next = fields[(index + 1) % fields.length];
    return this.focus(next.id);
  }

  focusPrev() {
    const fields = this.enabledFields();
    if (fields.length === 0) return false;
    const index = fields.findIndex((field) => field.id === this.activeId);
    const prev = fields[(index <= 0 ? fields.length : index) - 1];
    return this.focus(prev.id);
  }

  focusNextGroup() {
    const fields = this.enabledFields();
    if (fields.length === 0) return false;

    const current = this.fields.get(this.activeId || "");
    const currentGroup = current?.group;
    const groups: string[] = [];
    for (const field of fields) {
      const group = field.group || "__default";
      if (!groups.includes(group)) groups.push(group);
    }

    if (groups.length === 0) return this.focusNext();

    const groupIndex = groups.indexOf(currentGroup || groups[0]);
    const nextGroup = groups[(groupIndex + 1) % groups.length];
    const first = fields.find((field) => (field.group || "__default") === nextGroup);
    return first ? this.focus(first.id) : false;
  }

  getActiveId() {
    return this.activeId;
  }
}

export const focusManager = new FocusManager();
